import type { TemplateAuthMethod } from "@/types";

/**
 * What the bound connection contributes to an HTTP request, projected for builder display: the
 * base URL a relative service URL composes onto (ADR-030) and the headers the connection injects.
 * Everything here is the template's own BBEL source, shown verbatim — never resolved values. The
 * auth method lives on the connection (project-bound), so at workflow level a template with
 * several methods is only displayable where the methods agree; where they disagree the caller
 * falls back (slot-name chip, "varies" rows) rather than guessing.
 */

/** The template's base URL when unambiguous across auth methods (single method, or all agree). */
export function uniqueBaseUrl(methods: TemplateAuthMethod[]): string | null {
  const urls = [...new Set(methods.map((m) => (m.baseUrl ?? "").trim()).filter(Boolean))];
  return urls.length === 1 ? urls[0] : null;
}

/** Per-method base URLs for the disagreeing-methods tooltip. */
export function baseUrlVariants(methods: TemplateAuthMethod[]): Array<{ method: string; baseUrl: string }> {
  return methods
    .filter((m) => (m.baseUrl ?? "").trim())
    .map((m) => ({ method: m.label ?? m.id, baseUrl: m.baseUrl!.trim() }));
}

export interface GhostHeaderRow {
  /** Display name — a real header name, or "Authentication" for the collapsed generic row. */
  name: string;
  /** The expression when every method agrees; null = varies (the tooltip lists variants). */
  value: string | null;
  /** Per-method contributions backing the row (real header name + expression). */
  variants: Array<{ method: string; name: string; value: string }>;
}

/**
 * The readonly header rows the connection contributes, deduped by name across auth methods:
 * all methods inject the same names → one row per name (expression verbatim when they agree on
 * it too, "varies" when not); methods disagree on which headers exist at all → one generic
 * "Authentication" row carrying every contribution in its variants.
 */
export function ghostHeaderRows(methods: TemplateAuthMethod[]): GhostHeaderRow[] {
  const headersOf = (m: TemplateAuthMethod) => m.outboundHeaders ?? {};
  const withHeaders = methods.filter((m) => Object.keys(headersOf(m)).length > 0);
  if (withHeaders.length === 0) return [];

  const nameSets = new Set(withHeaders.map((m) => Object.keys(headersOf(m)).sort().join("\n")));
  if (nameSets.size > 1 || withHeaders.length !== methods.length) {
    return [
      {
        name: "Authentication",
        value: null,
        variants: withHeaders.flatMap((m) =>
          Object.entries(headersOf(m)).map(([name, value]) => ({ method: m.label ?? m.id, name, value })),
        ),
      },
    ];
  }

  return Object.keys(headersOf(withHeaders[0])).map((name) => {
    const values = [...new Set(withHeaders.map((m) => headersOf(m)[name]))];
    return {
      name,
      value: values.length === 1 ? values[0] : null,
      variants: withHeaders.map((m) => ({ method: m.label ?? m.id, name, value: headersOf(m)[name] })),
    };
  });
}

/** Header names the connection sets — a user header with one of these is overridden at run time. */
export function ghostHeaderNames(rows: GhostHeaderRow[]): Set<string> {
  return new Set(rows.flatMap((r) => r.variants.map((v) => v.name.toLowerCase())));
}

export interface ChipSegment {
  kind: "text" | "expr";
  value: string;
}

/** Split a base-URL template into literal and `{{ … }}` parts so the chip can colour expressions. */
export function chipSegments(text: string): ChipSegment[] {
  const out: ChipSegment[] = [];
  const re = /\{\{.*?\}\}/g;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push({ kind: "text", value: text.slice(last, m.index) });
    out.push({ kind: "expr", value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ kind: "text", value: text.slice(last) });
  return out;
}
